/* eslint-disable react/prop-types */
import React from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Typography, CircularProgress, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import UndoIcon from '@mui/icons-material/Undo';
import DescriptionIcon from '@mui/icons-material/Description';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import NoteIcon from '@mui/icons-material/Note';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';

const fmt = (v) => (v !== null && v !== undefined) ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : null;
const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const EVENT_CONFIG = {
  CREATED:            { icon: AddCircleOutlineIcon,    bg: '#e8f5e9', border: '#a5d6a7', icon_color: '#2e7d32', label_color: '#1b5e20', dot: '#4caf50' },
  RECEIVED:           { icon: CheckCircleOutlineIcon,  bg: '#e3f2fd', border: '#90caf9', icon_color: '#1565c0', label_color: '#0d47a1', dot: '#2196f3' },
  FINALIZED:          { icon: CheckCircleOutlineIcon,  bg: '#e3f2fd', border: '#90caf9', icon_color: '#1565c0', label_color: '#0d47a1', dot: '#2196f3' },
  PAYMENT:            { icon: PaymentIcon,             bg: '#f3e5f5', border: '#ce93d8', icon_color: '#6a1b9a', label_color: '#4a148c', dot: '#9c27b0' },
  PAYMENT_CANCELLED:  { icon: MoneyOffIcon,            bg: '#fff3e0', border: '#ffcc80', icon_color: '#e65100', label_color: '#bf360c', dot: '#ff9800' },
  RETURN:             { icon: UndoIcon,                bg: '#fff8e1', border: '#ffe082', icon_color: '#f57f17', label_color: '#e65100', dot: '#ffc107' },
  DOCUMENT:           { icon: DescriptionIcon,         bg: '#fce4ec', border: '#f48fb1', icon_color: '#880e4f', label_color: '#880e4f', dot: '#e91e63' },
  SETTLED:            { icon: HandshakeOutlinedIcon,   bg: '#e8f5e9', border: '#a5d6a7', icon_color: '#2e7d32', label_color: '#1b5e20', dot: '#4caf50' },
  CANCELLED:          { icon: CancelIcon,              bg: '#ffebee', border: '#ef9a9a', icon_color: '#b71c1c', label_color: '#b71c1c', dot: '#f44336' },
  CREDIT_NOTE:        { icon: ReceiptIcon,             bg: '#e0f2f1', border: '#80cbc4', icon_color: '#004d40', label_color: '#004d40', dot: '#009688' },
  DEBIT_NOTE:         { icon: NoteIcon,                bg: '#fff3e0', border: '#ffcc80', icon_color: '#e65100', label_color: '#bf360c', dot: '#ff9800' },
};

function TimelineEvent({ event, isLast }) {
  const cfg = EVENT_CONFIG[event.type] || EVENT_CONFIG.CREATED;
  const Icon = cfg.icon;

  return (
    <Box sx={{ display: 'flex', gap: 0, position: 'relative' }}>
      {/* Spine + dot */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '50%', background: cfg.bg,
          border: `2px solid ${cfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, zIndex: 1, boxShadow: `0 0 0 3px ${cfg.dot}22`,
        }}>
          <Icon sx={{ fontSize: 18, color: cfg.icon_color }} />
        </Box>
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, background: `linear-gradient(to bottom, ${cfg.dot}55, ${cfg.dot}11)`, mt: 0.5, mb: 0.5, minHeight: 20 }} />
        )}
      </Box>

      {/* Content card */}
      <Box sx={{
        flex: 1, mb: isLast ? 0 : 2,
        p: 1.5, borderRadius: 2,
        background: cfg.bg, border: `1px solid ${cfg.border}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: cfg.label_color, lineHeight: 1.3 }}>
            {event.label}
          </Typography>
          {event.amount !== null && event.amount !== undefined && event.amount > 0 && (
            <Chip
              label={fmt(event.amount)}
              size="small"
              sx={{
                fontFamily: 'monospace', fontWeight: 700, fontSize: '0.72rem',
                height: 20, background: cfg.border, color: cfg.label_color,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {event.description && (
          <Typography sx={{ fontSize: '0.72rem', color: '#555', mt: 0.4, lineHeight: 1.4 }}>
            {event.description}
          </Typography>
        )}
        {event.reference && (
          <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#888', mt: 0.3 }}>
            Ref: {event.reference}
          </Typography>
        )}

        <Typography sx={{ fontSize: '0.63rem', color: '#aaa', mt: 0.6, letterSpacing: 0.2 }}>
          {fmtDateTime(event.at)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function TimelineDialog({ open, onClose, title, subtitle, events, loading }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', maxHeight: '88vh' } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 60%, #3949ab 100%)',
          px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white !important' }}>{title}</Typography>
            </Box>
            {subtitle && (
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65) !important', mt: 0.3 }}>{subtitle}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '20px !important', overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#3949ab' }} />
          </Box>
        ) : !events?.length ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#bbb' }}>
            <TimelineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography sx={{ fontSize: '0.85rem' }}>No timeline events found</Typography>
          </Box>
        ) : (
          <Box sx={{ px: 0.5, pt: 0.5 }}>
            {events.map((ev, i) => (
              <TimelineEvent key={ev.id} event={ev} isLast={i === events.length - 1} />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid #f0f0f0' }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#bbb', flex: 1 }}>
          {events?.length ? `${events.length} event${events.length !== 1 ? 's' : ''}` : ''}
        </Typography>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#555 !important', fontSize: '0.8rem' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
